// Redux store initialization
import { createSlice, configureStore } from '@reduxjs/toolkit';

// Auth Slice
const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    userRole: null,
    loading: false,
    error: null,
  },
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setUserRole: (state, action) => {
      state.userRole = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.userRole = null;
      state.error = null;
    },
  },
});

// Search Slice
const searchSlice = createSlice({
  name: 'search',
  initialState: {
    source: '',
    destination: '',
    journeyDate: '',
    returnDate: '',
    passengers: 1,
    trainClass: 'All',
    results: [],
    loading: false,
  },
  reducers: {
    setSearch: (state, action) => {
      state.source = action.payload.source;
      state.destination = action.payload.destination;
      state.journeyDate = action.payload.journeyDate;
      state.returnDate = action.payload.returnDate || '';
      state.passengers = action.payload.passengers || 1;
    },
    setTrainClass: (state, action) => {
      state.trainClass = action.payload;
    },
    setResults: (state, action) => {
      state.results = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    clearSearch: (state) => {
      state.source = '';
      state.destination = '';
      state.journeyDate = '';
      state.results = [];
    },
  },
});

// Booking Slice
const bookingSlice = createSlice({
  name: 'booking',
  initialState: {
    selectedTrain: null,
    selectedSeats: [],
    passengers: [],
    totalFare: 0,
    loading: false,
    bookingConfirmed: false,
    pnr: null,
  },
  reducers: {
    setSelectedTrain: (state, action) => {
      state.selectedTrain = action.payload;
    },
    addSelectedSeat: (state, action) => {
      const seat = action.payload;
      if (!state.selectedSeats.find(s => s.coachNumber === seat.coachNumber && s.seatNumber === seat.seatNumber)) {
        state.selectedSeats.push(seat);
      }
    },
    removeSelectedSeat: (state, action) => {
      state.selectedSeats = state.selectedSeats.filter(
        s => !(s.coachNumber === action.payload.coachNumber && s.seatNumber === action.payload.seatNumber)
      );
    },
    setPassengers: (state, action) => {
      state.passengers = action.payload;
    },
    setTotalFare: (state, action) => {
      state.totalFare = action.payload;
    },
    setBookingConfirmed: (state, action) => {
      state.bookingConfirmed = action.payload;
    },
    setPNR: (state, action) => {
      state.pnr = action.payload;
    },
    resetBooking: (state) => {
      state.selectedTrain = null;
      state.selectedSeats = [];
      state.passengers = [];
      state.totalFare = 0;
      state.bookingConfirmed = false;
      state.pnr = null;
    },
  },
});

// UI Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState: {
    sidebarOpen: true,
    darkMode: false,
    notifications: [],
    notificationCount: 0,
  },
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    toggleDarkMode: (state) => {
      state.darkMode = !state.darkMode;
    },
    addNotification: (state, action) => {
      state.notifications.push(action.payload);
      state.notificationCount = state.notifications.filter(n => !n.read).length;
    },
    removeNotification: (state, action) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
      state.notificationCount = state.notifications.filter(n => !n.read).length;
    },
  },
});

// Export actions
export const { setUser, setUserRole, setLoading, setError, logout } = authSlice.actions;
export const { setSearch, setTrainClass, setResults, clearSearch } = searchSlice.actions;
export const {
  setSelectedTrain,
  addSelectedSeat,
  removeSelectedSeat,
  setPassengers,
  setTotalFare,
  setBookingConfirmed,
  setPNR,
  resetBooking,
} = bookingSlice.actions;
export const { toggleSidebar, toggleDarkMode, addNotification, removeNotification } = uiSlice.actions;

// Configure store
export const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
    search: searchSlice.reducer,
    booking: bookingSlice.reducer,
    ui: uiSlice.reducer,
  },
});

export default store;
